/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { apiCall } from 'app/shared/lib/api/api';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { getCustomFieldTargetColumns, ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldDefinition, CustomFieldTarget } from 'app/core/models/essette-api';
import { Sorting } from 'app/shared/lib/essette-site';
import { localSort } from 'app/shared/lib/sorting';

const customFieldDefinitionsMap = new Map<string, CustomFieldTarget>();

/**
 * A context class for caching and retrieving custom field target information
 */
@autoinject()
export class CustomFieldContext {

    ////****************** Fields and Properties **************************************////
    private sessionEvent: Subscription | null = null;

    private localSorting = new Sorting();

    ////****************** Lifecycle ***********************************************////
    constructor(
        protected readonly service: CustomFieldService,
        protected readonly env: EnvironmentService,
        protected readonly events: EventAggregator) {

        this.sessionEvent = this.events.subscribe(SessionChangedEvent,
            (e: SessionChangedEvent) => {
                if(['checkIn', 'logIn'].includes(e.key)) {
                    // if user context has changed (tenant, context) due to logIn/logOut/checkIn/etc.
                    this.clear();
                    this.load();
                }
            });
    }

    ////****************** Functions ***********************************************////
    /**
     * Load all custom field definitions for the tenant and cache them
     */
    public async load(): Promise<void> {
        if((!customFieldDefinitionsMap || customFieldDefinitionsMap.size === 0) &&
            this.env.tenant.id &&
            this.env.application.code !== "Portal.Providers") {
            // Ensure we have a tenant.  On a SessionChangeEvent "logIn" may, or maynot, have a tenant depending on whether a "checkIn" is required.
            // Currently we do not have API permissions for portal.  There is no need to load these.
            await this.loadCustomFieldDefinitions();
        }
    }

    protected clear(): void {
        customFieldDefinitionsMap.clear();
    }

    private mapCustomFieldDefinitions(targets: Array<CustomFieldTarget>): void {
        targets.forEach(target => {
            if(target.code) {
                customFieldDefinitionsMap.set(target.code, target);
            }
        });
    }

    /**
     * Get the custom field definition for a model
     * @param modelCode A model code associated to a custom field definition
     * @returns A custom field definition, including its defined fields
     */
    public async getCustomFieldDefinition(modelCode: string): Promise<CustomFieldDefinition | null> {
        await this.load();
        return customFieldDefinitionsMap.get(modelCode) ?? null;
    }

    /**
     * Get the custom field definitions for a model's target
     * @param modelCode A model code associated to a custom field target
     * @returns An array of Custom Field Definitions defined for the model's target
     */
    public async getCustomFieldDefinitions(modelCode: string): Promise<CustomFieldDefinition[]> {
        await this.load();
        return customFieldDefinitionsMap.get(modelCode)?.definitions ?? [];
    }

    /**
     * Get Custom Field Target Column(UDF) information for use with `ess-table`
     * @param modelCode A model code associated to a custom field definition
     * @param settings.definitionCount Specifies and limits the size of the "Text" and "Lookup" column arrays, where the array indexes match the respective definition's index value
     * @param settings.filterInactive If true, inactive fields will be filtered out of the returned target columns
     * @param settings.sortByLabel If true, fields will be locally sorted alphabetical ascending by singular label
     * @returns A container containing text and lookup arrays of Custom Field column definitions with extended info for use with `ess-table`
     */
    public async getCustomFieldTargetColumns(modelCode: string, settings?: ICustomFieldTargetColumnsSettings): Promise<ICustomFieldTargetColumnsContainer> {
        if(settings?.refreshCache) {
            this.clear();
        }
        const columnsContainer: ICustomFieldTargetColumnsContainer = getCustomFieldTargetColumns(await this.getCustomFieldDefinitions(modelCode), settings?.definitionCount);
        if(settings?.filterInactive) {
            columnsContainer.text = columnsContainer.text?.filter(field => field.isActive);
            columnsContainer.lookup = columnsContainer.lookup?.filter(field => field.isActive);
        }
        if(settings?.sortByLabel) {
            this.localSorting.add({ reference: 'label' });
            if(columnsContainer.text) localSort(columnsContainer.text, this.localSorting);
            if(columnsContainer.lookup) localSort(columnsContainer.lookup || [], this.localSorting);
        }
        return columnsContainer;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadCustomFieldDefinitions(): Promise<void> {
        try {
            const response = await this.service.loadCustomFieldTargets();
            this.mapCustomFieldDefinitions(response.results);
        }
        catch(error) {
            throw error;
        }
    }

}
